import { Component, inject } from "@angular/core";
import { Router } from "@angular/router";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { AppButtonComponent } from "../../../../Common/Components/Button/ButtonComponent";
import { AppCardComponent } from "../../../../Common/Components/Card/CardComponent";
import { LocationSelectorComponent } from "../../../../Common/Components/LocationSelector/LocationSelectorComponent";
import { AutocompleteComponent } from "../../../../Common/Components/Autocomplete/AutocompleteComponent";
import { LocationAddress } from "../../../../Common/Components/LocationSelector/Type/LocationSelectorTypes";
import { PartnerEffects } from "../../State/PartnerEffects";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { CreatePartnerRequest } from "../../Dto/CreatePartnerRequest";
import { PartnerType } from "../../Dto/PartnerModel";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";

@Component({
  selector: "app-partner-create",
  standalone: true,
  imports: [ReactiveFormsModule, AppButtonComponent, AppCardComponent, LocationSelectorComponent, AutocompleteComponent],
  templateUrl: "./PartnerCreatePage.html",
  styleUrl: "./PartnerCreatePage.scss",
})
export class PartnerCreatePage {
  private readonly router = inject(Router);
  private readonly partnerEffects = inject(PartnerEffects);
  private readonly toastEffects = inject(ToastEffects);
  private readonly fb = inject(FormBuilder);

  typeOptions = Object.values(PartnerType).map((type) => ({
    value: type,
    label: type,
  }));

  partnerForm: FormGroup;
  isSaving = false;

  constructor() {
    this.partnerForm = this.fb.group({
      name: ["", [Validators.required, Validators.minLength(2)]],
      type: ["", [Validators.required]],
      address: this.fb.group({
        stateProvinceRegion: ["", [Validators.required]],
        countyDistrict: ["", [Validators.required]],
        subdistrictMunicipality: ["", [Validators.required]],
        communeVillage: ["", [Validators.required]],
        line: [""],
        zip: [""],
        code: [""],
        longitude: [null],
        latitude: [null],
      }),
    });
  }

  async savePartner(): Promise<void> {
    if (this.partnerForm.invalid) {
      this.partnerForm.markAllAsTouched();
      this.toastEffects.show("Please fill in all required fields.", "error");
      return;
    }

    this.isSaving = true;

    const formData = this.partnerForm.value;
    const payload: CreatePartnerRequest = {
      name: formData.name,
      type: formData.type,
      address: {
        stateProvinceRegion: formData.address.stateProvinceRegion,
        countyDistrict: formData.address.countyDistrict,
        subdistrictMunicipality: formData.address.subdistrictMunicipality,
        communeVillage: formData.address.communeVillage,
        line: formData.address.line || "",
        zip: formData.address.zip || "",
        code: formData.address.code || "",
        longitude: formData.address.longitude ?? null,
        latitude: formData.address.latitude ?? null,
      },
    };

    try {
      await this.partnerEffects.createPartner(payload);
      this.toastEffects.show("Partner created successfully.", "success");
      await this.router.navigate(["/partners"]);
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to create partner."), "error");
    } finally {
      this.isSaving = false;
    }
  }

  goBack(): void {
    this.router.navigate(["/partners"]).catch(() => undefined);
  }

  onTypeChange(value: string | number | (string | number)[]): void {
    if (Array.isArray(value)) {
      return;
    }
    this.partnerForm.patchValue({ type: value });
  }

  onAddressChange(value: LocationAddress): void {
    this.partnerForm.patchValue({
      address: {
        stateProvinceRegion: value.stateProvinceRegion,
        countyDistrict: value.countyDistrict,
        subdistrictMunicipality: value.subdistrictMunicipality,
        communeVillage: value.communeVillage,
        line: value.line,
        zip: value.zip,
        code: value.code,
        longitude: value.longitude,
        latitude: value.latitude,
      },
    });
  }
}
